import { PAPEIS } from '@locaveri/shared'
import { useNavigate } from '@tanstack/react-router'
import { CircleUser, LogOut, ShieldCheck, ShieldOff } from 'lucide-react'
import type { ReactElement } from 'react'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { encerrarSessao } from '@/lib/sessao'
import { initials } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'

import { AVISO_MODULO_FUTURO } from './nav-items'

/**
 * Menu da sessão.
 *
 * "Sair" avisa o servidor para revogar a família do refresh token e só então
 * limpa o cliente — sair só no navegador deixaria um refresh válido em pé.
 * "Sair de todas as sessões" é o botão de pânico: notebook esquecido, celular
 * perdido, desligamento de funcionário.
 */
export function UserMenu(): ReactElement {
  const usuario = useAuthStore((estado) => estado.usuario)
  const navigate = useNavigate()

  const nome = usuario?.nome ?? 'Visitante'
  const email = usuario?.email ?? 'sessão não iniciada'

  function sair(todasAsSessoes: boolean): void {
    void encerrarSessao({ todasAsSessoes }).then(() => navigate({ to: '/login' }))
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Menu de ${nome}`} className="rounded-full">
          <Avatar>
            <AvatarFallback>{initials(nome)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Conta</DropdownMenuLabel>

        <div className="px-2 pb-2">
          <p className="truncate text-sm font-medium text-foreground">{nome}</p>
          <p className="truncate text-xs text-fg-secondary">{email}</p>

          {usuario === null ? null : (
            <>
              <p className="mt-1 truncate text-xs text-fg-muted">
                {usuario.papeis.map((papel) => PAPEIS[papel].nome).join(' · ')}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-fg-muted">
                {usuario.totpAtivo ? (
                  <>
                    <ShieldCheck className="size-3.5" aria-hidden="true" />
                    Verificação em duas etapas ativa
                  </>
                ) : (
                  <>
                    <ShieldOff className="size-3.5" aria-hidden="true" />
                    Sem verificação em duas etapas
                  </>
                )}
              </p>
            </>
          )}
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem aria-disabled="true" disabled title={AVISO_MODULO_FUTURO}>
          <CircleUser aria-hidden="true" />
          Meu perfil
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={() => {
            sair(false)
          }}
        >
          <LogOut aria-hidden="true" />
          Sair
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={() => {
            sair(true)
          }}
        >
          <LogOut aria-hidden="true" />
          Sair de todas as sessões
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
